import {AgGridReact, AgGridReactProps} from '@ag-grid-community/react';
import {
  DriverActivityEntity,
  GeofenceEventEntity,
  MessageEntity,
  PersonEntity,
  Supplemental
} from "@ots-frontend/ots-data";
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {
  CellClickedEvent,
  GetRowIdFunc,
  GridOptions,
  GridReadyEvent,
  RowClassRules,
  RowHeightParams,
  RowSelectedEvent,
  RowSelectionOptions
} from "@ag-grid-community/core";
import {useDriverActivityCols} from "./useDriverActivityCols";
import {useDriverActivityContextMenu} from "./DriverActivityContextMenu/useDriverActivityContextMenu";
import {DriverActivityContextMenu} from "./DriverActivityContextMenu/DriverActivityContextMenu";
import type {
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from "@ag-grid-community/core/dist/types/src/interfaces/autoSizeStrategy";
import {GeofenceEventModal} from "./modals/GeofenceEventModal/GeofenceEventModal";
import {Extras, ExtrasModal} from "./modals/ExtrasModal/ExtrasModal";

export type DriverActivityGridProps = Omit<AgGridReactProps<DriverActivityEntity>, 'rowData' | 'colDefs'> & {
  data?: DriverActivityEntity[]
  hiddenPersons?: string[]
  onPersonToggle?: (person: PersonEntity) => void
}

export const DriverActivityGrid = forwardRef<AgGridReact<DriverActivityEntity>, DriverActivityGridProps>((props, ref) => {
  const {
    data,
    hiddenPersons,
    onPersonToggle,
    onRowSelected,
    ...rest
  } = props

  const _ref = useRef<AgGridReact<DriverActivityEntity>>(null);
  useImperativeHandle(ref, () => _ref.current!, []);

  const [selectedRows, setSelectedRows] = useState<DriverActivityEntity[]>([])
  const [selectedGeofenceEvent, setSelectedGeofenceEvent] = useState<GeofenceEventEntity | undefined>(undefined);
  const [selectedExtras, setSelectedExtras] = useState<Extras | undefined>(undefined);

  const {
    colDefs,
  } = useDriverActivityCols({
    hiddenPersons: hiddenPersons
  })

  const {
    isOpen,
    renderLayer,
    layerProps,
    contextMenu,
    onCellContextMenu,
  } = useDriverActivityContextMenu()

  const getRowId: GetRowIdFunc<DriverActivityEntity> = useCallback((params) => {
    return params.data.type && params.data.id ? (params.data.type + "_" + params.data.id) : "-1"
  }, [])

  const getRowHeight = useCallback((params: RowHeightParams<DriverActivityEntity>): number | undefined | null => {
    const dataType = params.data?.type
    if (dataType === "person") {
      return 84
    } else if (dataType === "endShift") {
      return 63
    } else if (dataType === "startShift") {
      return 42
    }
    return undefined
  }, [])

  const gridOptions: GridOptions<DriverActivityEntity> = useMemo(() => {
    return {
      getRowHeight: getRowHeight,
      suppressHorizontalScroll: true,
    }
  }, [getRowHeight])

  const rowSelection: RowSelectionOptions = useMemo(() => {
    return {
      mode: 'multiRow',
      checkboxes: false,
      headerCheckbox: false,
      enableClickSelection: true,
    }
  }, [])

  const onCellClick = useCallback((event: CellClickedEvent<DriverActivityEntity>) => {
    if (event.column.getColId() === "pickupGeofenceEvent") {
      if (event.data?.person) {
        if (onPersonToggle) {
          onPersonToggle(event.data.person)
        }
      } else if (event.data?.tta || event.data?.pickupTransport) {
        const transport = event.data?.tta?.pickupTransport ?? event.data?.pickupTransport
        if (transport?.geofenceEvent) {
          setSelectedGeofenceEvent(transport.geofenceEvent)
        }
      }
    } else if (event.column.getColId() === "deliveryGeofenceEvent") {
      if (event.data?.tta || event.data?.deliveryTransport) {
        const transport = event.data?.tta?.deliveryTransport ?? event.data?.deliveryTransport
        if (transport?.geofenceEvent) {
          setSelectedGeofenceEvent(transport.geofenceEvent)
        }
      }
    } else if (event.column.getColId() === "extras") {
      let messages: MessageEntity[] = []
      let supplementals: Supplemental[] = []

      if (event.data?.tta) {
        let transport = event.data.tta.pickupTransport
        if (transport) {
          if (transport.messages) {
            messages.push(...transport.messages)
          }
          if (transport.supplementals) {
            supplementals.push(...transport.supplementals)
          }
        }
        transport = event.data.tta.deliveryTransport
        if (transport) {
          if (transport.messages) {
            messages.push(...transport.messages)
          }
          if (transport.supplementals) {
            supplementals.push(...transport.supplementals)
          }
        }
      } else if (event.data?.transport) {
        const transport = event.data.transport
        if (transport.messages) {
          messages.push(...transport.messages)
        }
        if (transport.supplementals) {
          supplementals.push(...transport.supplementals)
        }
      } else if (event.data?.shift) {
        const shift = event.data.shift
        if (shift.messages) {
          messages.push(...shift.messages)
        }
      }


      const extras: Extras = {
        messages: messages,
        supplementals: supplementals,
      }

      setSelectedExtras(extras)
    }
  }, [onPersonToggle])

  const _onRowSelected = useCallback((event: RowSelectedEvent<DriverActivityEntity>) => {
    setSelectedRows((prev) => {
      const selections: DriverActivityEntity[] = [...prev]

      const data = event.data
      if (data) {
        const foundIndex = prev.findIndex(a => a.id === data.id)
        if (event.node.isSelected()) {
          if (foundIndex === -1) {
            selections.push(data)
          }
        } else {
          if (foundIndex >= 0) {
            selections.splice(foundIndex, 1)
          }
        }
      }


      return selections
    })

    if (onRowSelected) {
      onRowSelected(event)
    }
  }, [onRowSelected])

  const rowClassRules: RowClassRules<DriverActivityEntity> = useMemo(() => {
    return {
      "dad-person": (params) => params.data?.type === "person",
      "dad-start-shift": (params) => params.data?.type === "startShift",
      "dad-end-shift": (params) => params.data?.type === "endShift",
      "dad-geofence-event": (params) => params.data?.type === "geofenceEvent",
      "dad-dispatch": (params) => params.data?.type === "dispatch",
      "dad-service-work": (params) => {
        const data = params.data
        const type = data?.type
        if (type === "tta") {
          return typeof data?.tta?.pickupTransport?.serviceTypeId !== "undefined"
        } else if (type === "pickupTransport") {
          return typeof data?.pickupTransport?.serviceTypeId !== "undefined"
        } else if (type === "deliveryTransport") {
          return typeof data?.deliveryTransport?.serviceTypeId !== "undefined"
        }
        return false
      },
    }
  }, [])

  const autoSizeStrategy: SizeColumnsToFitGridStrategy | SizeColumnsToFitProvidedWidthStrategy | SizeColumnsToContentStrategy = useMemo(() => {
    return {
      type: "fitGridWidth",
      columnLimits: [{
        colId: 'pickupSite',
        minWidth: 200
      }, {
        colId: 'deliverySite',
        minWidth: 200
      }
      ]
    };
  }, [])

  const filteredData: DriverActivityEntity[] | undefined = useMemo(() => {
    const filtered: DriverActivityEntity[] = []

    if (data) {
      const personSet = hiddenPersons ? new Set<string>(hiddenPersons) : new Set<string>()
      for (const activity of data) {
        activity.hidden = typeof activity.personId !== "undefined" && personSet.has(activity.personId)
        if (activity.type !== "person" && activity.hidden) {
          continue
        }

        filtered.push(activity)
      }
    }

    return filtered
  }, [data, hiddenPersons])

  useEffect(() => {
    _ref?.current?.api?.refreshCells({columns: ["pickupGeofenceEvent"]})
  }, [hiddenPersons]);

  const onGridReady = useCallback((params: GridReadyEvent<DriverActivityEntity>) => {
    // in this example, the CSS styles are loaded AFTER the grid is created,
    // so we put this in a timeout, so height is calculated after styles are applied.
    setTimeout(() => {
      params.api.setGridOption("rowData", filteredData);
    }, 500);
  }, [filteredData]);

  return (
    <div className="ag-theme-quartz" style={{height: "100%", width: "100%"}}>
      <AgGridReact
        {...rest}
        ref={_ref}
        rowData={filteredData}
        columnDefs={colDefs}
        getRowId={getRowId}
        enableCellTextSelection={true}
        ensureDomOrder={true}
        onCellClicked={onCellClick}
        onCellContextMenu={onCellContextMenu}
        onRowSelected={_onRowSelected}
        preventDefaultOnContextMenu={true}
        gridOptions={gridOptions}
        rowClassRules={rowClassRules}
        rowSelection={rowSelection}
        autoSizeStrategy={autoSizeStrategy}
        onGridReady={onGridReady}/>

      {(isOpen && contextMenu) &&
        renderLayer(
          <div {...layerProps}>
            <DriverActivityContextMenu
              {...contextMenu}
              context={selectedRows}/>
          </div>
        )
      }

      {selectedGeofenceEvent &&
        <GeofenceEventModal
          centered={true}
          geofenceEvent={selectedGeofenceEvent}
          opened={true}
          onClose={() => setSelectedGeofenceEvent(undefined)}/>
      }

      {selectedExtras &&
        <ExtrasModal
          centered={true}
          extras={selectedExtras}
          opened={true}
          onClose={() => setSelectedExtras(undefined)}/>
      }

    </div>
  )
})
